import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DailyNutrients, FoodItem, MealType } from '@/types/meal-plan';
import NutrientCharts from './NutrientCharts';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { format } from 'date-fns';
import { Separator } from '@/components/ui/separator';
import { ActivityLevel } from '@/types/client';
import { Link } from 'lucide-react';
import InlineFeedback from '@/components/shared/InlineFeedback';
import '../css/DietReport.css';

interface ClientProfile {
  sex: 'male' | 'female';
  age: number;
  height: number; // in cm
  weight: number; // in kg
  activityLevel: ActivityLevel;
  goals: string;
}

interface DietReportModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  meals: {
    [day: number]: {
      [mealType: string]: FoodItem[];
    };
  };
  nutrientsByDay: {
    [day: number]: DailyNutrients;
  };
  planDuration: number;
  nutrientGuideline: string;
  clientProfile?: ClientProfile;
  mealTypes?: string[]; // Actual meal types from API
  planAverageNutrition?: DailyNutrients; // Plan-level average from API
}

// Helper function to get meal type display name
const getMealTypeDisplayName = (mealType: string): string => {
  // Convert camelCase or snake_case to Title Case
  return mealType
    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
    .replace(/_/g, ' ') // Replace underscores with spaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();
};


const DietReportModal: React.FC<DietReportModalProps> = ({ 
  open, 
  onOpenChange,
  meals,
  nutrientsByDay,
  planDuration,
  nutrientGuideline,
  clientProfile,
  mealTypes,
  planAverageNutrition
}) => {
  // Use API data directly - no fallback calculation
  const days = Array.from({ length: planDuration }, (_, i) => i + 1);
  const [copyLinkText, setCopyLinkText] = useState('Copy Link');
  const [copyFeedback, setCopyFeedback] = useState<{
    variant: 'error' | 'success';
    title: string;
    message: string;
  } | null>(null);
  

  const activeMealTypes = mealTypes;
  const currentDate = format(new Date(), 'PPP');
  const currentTime = format(new Date(), 'p');

  const handleCopyLink = async () => {
    // Generate a shareable link - in a real app this would create a unique URL
    const shareableLink = `${window.location.origin}/shared-report/${Date.now()}`;
    try {
      await navigator.clipboard.writeText(shareableLink);
      setCopyLinkText('Copied!');
      setCopyFeedback({
        variant: 'success',
        title: 'Link copied',
        message: 'Shareable link has been copied to clipboard.',
      });

      setTimeout(() => {
        setCopyLinkText('Copy Link');
      }, 2000);
    } catch {
      setCopyFeedback({
        variant: 'error',
        title: 'Could not copy link',
        message: 'Clipboard access is unavailable. Please try again.',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex justify-between items-center">
            <span>Diet Report</span>
            <Button onClick={handleCopyLink} variant="outline" size="sm" className="gap-1">
              <Link size={16} />
              {copyLinkText}
            </Button>
          </DialogTitle>
          <DialogDescription>
            Comprehensive nutritional analysis based on the created meal plan
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 dialog-content print-full-width">
          {copyFeedback ? (
            <InlineFeedback
              variant={copyFeedback.variant}
              title={copyFeedback.title}
              message={copyFeedback.message}
            />
          ) : null}
          {/* Branding and Timestamp */}
          <div className="flex justify-between items-center text-sm text-gray-500 pb-2 border-b">
            <div className="font-semibold">NutritionPro™</div>
            <div>{currentDate} at {currentTime}</div>
          </div>

          {/* Client Profile Section */}
          {clientProfile && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-3">Client Profile</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <div>
                    <p className="text-sm text-gray-500">Sex</p>
                    <p className="font-medium">{clientProfile.sex === 'male' ? 'Male' : 'Female'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Age</p>
                    <p className="font-medium">{clientProfile.age} years</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Height</p>
                    <p className="font-medium">{clientProfile.height} cm</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Weight</p>
                    <p className="font-medium">{clientProfile.weight} kg</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Activity Level</p>
                    <p className="font-medium capitalize">{clientProfile.activityLevel.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="col-span-2 md:col-span-1">
                    <p className="text-sm text-gray-500">Goals</p>
                    <p className="font-medium">{clientProfile.goals}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Calculation Parameters */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-3">Calculation Parameters</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Nutrient Guidelines</p>
                  <p className="font-medium">{nutrientGuideline}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weekly Summary */}
          {planAverageNutrition && (
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-semibold mb-4">Weekly Average vs Targets</h3>
                <NutrientCharts 
                  nutrients={planAverageNutrition}
                  targets={clientProfile ? {
                    calories: (clientProfile as any).calorieGoal || (clientProfile as any).caloriesPerDay,
                    protein: (clientProfile as any).proteinGoal,
                    carbs: (clientProfile as any).carbsGoal,
                    fat: (clientProfile as any).fatGoal,
                    vitaminA: (clientProfile as any).vitaminAGoal,
                    vitaminC: (clientProfile as any).vitaminCGoal,
                    calcium: (clientProfile as any).calciumGoal,
                    iron: (clientProfile as any).ironGoal,
                  } : undefined}
                />
              </CardContent>
            </Card>
          )}

          {/* Daily Breakdown */}
          <Card>
            <CardContent className="pt-6">
              <h3 className="text-lg font-semibold mb-4">Daily Meal Plan Breakdown</h3>
              <Tabs defaultValue={days[0].toString()}>
                <TabsList className="w-full flex overflow-x-auto">
                  {days.map(day => (
                    <TabsTrigger key={day} value={day.toString()} className="flex-1">
                      Day {day}
                    </TabsTrigger>
                  ))}
                </TabsList>
                {days.map(day => (
                  <TabsContent key={day} value={day.toString()} className={day > 1 ? "page-break" : ""}>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Meal Schedule */}
                        <div>
                          <h4 className="text-md font-medium mb-2">Meal Schedule</h4>
                          {activeMealTypes.map(mealType => (
                            <div key={mealType} className="mb-4">
                              <h5 className="font-medium text-sm">{getMealTypeDisplayName(mealType)}</h5>
                              {meals[day]?.[mealType]?.length ? (
                                <Table>
                                  <TableHeader>
                                    <TableRow>
                                      <TableHead>Food</TableHead>
                                      <TableHead className="w-24 text-right">Serving</TableHead>
                                    </TableRow>
                                  </TableHeader>
                                  <TableBody>
                                    {meals[day][mealType].map((item, index) => (
                                      <TableRow key={index}>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell className="text-right">{item.serving}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              ) : (
                                <p className="text-sm text-gray-500">No items added</p>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Nutrient Summary */}
                        <div>
                          <h4 className="text-md font-medium mb-2">Nutrient Summary</h4>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Nutrient</TableHead>
                                <TableHead className="text-right">Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              <TableRow>
                                <TableCell>Calories</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.calories || 0} kcal</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Protein</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.protein || 0} g</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Carbs</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.carbs || 0} g</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Fat</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.fat || 0} g</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Vitamin A</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.micronutrients.vitaminA || 0} μg</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Vitamin C</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.micronutrients.vitaminC || 0} mg</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Calcium</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.micronutrients.calcium || 0} mg</TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell>Iron</TableCell>
                                <TableCell className="text-right">{nutrientsByDay[day]?.micronutrients.iron || 0} mg</TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>

                          {/* Daily Charts */}
                          <div className="mt-6">
                            <NutrientCharts 
                              nutrients={nutrientsByDay[day] || {
                                totalCalories: 0, totalProtein: 0, totalCarbs: 0, totalFat: 0, totalFiber: 0,
                                calories: 0, protein: 0, carbs: 0, fat: 0,
                                micronutrients: { vitaminA: 0, vitaminC: 0, calcium: 0, iron: 0, sodium: 0 }
                              }}
                              targets={clientProfile ? {
                                calories: (clientProfile as any).calorieGoal || (clientProfile as any).caloriesPerDay,
                                protein: (clientProfile as any).proteinGoal,
                                carbs: (clientProfile as any).carbsGoal,
                                fat: (clientProfile as any).fatGoal,
                                vitaminA: (clientProfile as any).vitaminAGoal,
                                vitaminC: (clientProfile as any).vitaminCGoal,
                                calcium: (clientProfile as any).calciumGoal,
                                iron: (clientProfile as any).ironGoal,
                              } : undefined}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </CardContent>
          </Card>

          {/* Footer with branding */}
          <div className="text-center text-sm text-gray-500 pt-4 border-t">
            <p>Generated by NutritionPro™ | {currentDate}</p>
            <p className="text-xs mt-1">All nutritional information is based on the {nutrientGuideline} guidelines.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DietReportModal;
